import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kBrowserSettingsCore, kKeyValueStorageLike } from "../../../Admin/kReusableCoreTS/kBrowserSettingsCore";

// AppBrowserSettings - the page's small remembered choices, over the SHARED `kBrowserSettingsCore` (shared-storage adoption 2026-10-05).
//
// WHAT THIS PRODUCT FIXES ABOUT THE SHARED CLASS: nothing is prefixed (the keys `wac.theme`, `wac.recent`, ... are the ones already in people's
// browsers) and the shared class's PAGE-MEMORY FALLBACK IS SWITCHED OFF. Before the adoption a refused write was simply not remembered, and a read
// afterwards saw whatever the storage held; with the fallback on, a refused write would answer the next read in the same page. That is a behaviour
// change this product does not want, so it is off here, always.
//
// ⛔ NOT FOR SECRETS. `AppArchiveStore` (the archive key) stays hand-written on purpose; see documentation/ai/SharedStorageAdoption-Status.md.
//
// A STORAGE IS FETCHED THROUGH A GETTER, because merely reading `window.localStorage` / `window.sessionStorage` throws in private browsing and under a
// blocked-storage policy (and `window` itself is absent in a test): the shared class catches that and answers "no storage".

export class AppBrowserSettings {
    /** Settings over the page's `localStorage`. */
    public static Local = (): kBrowserSettingsCore => {
        return AppBrowserSettings.Over(AppBrowserSettings.LocalStorage);
    };

    /** Settings over the tab's `sessionStorage`. */
    public static Session = (): kBrowserSettingsCore => {
        return AppBrowserSettings.Over(AppBrowserSettings.SessionStorage);
    };

    /** Settings over `globalThis.localStorage` (the translate memory reads it that way; undefined where there is none). */
    public static GlobalLocal = (): kBrowserSettingsCore => {
        return AppBrowserSettings.Over(AppBrowserSettings.GlobalLocalStorage);
    };

    /** Settings over any storage getter (a stand-in a test or a caller injects); the getter may throw. */
    public static Over = (Get: () => kKeyValueStorageLike | null | undefined): kBrowserSettingsCore => {
        const Core: kBrowserSettingsCore = kBrowserSettingsCore.FromAccessor(Get);
        if (getCoreUtils().KErrorMode === true) { return Core; }
        Core.MemoryFallback = false;
        return Core;
    };

    /** DOCUMENTED EXCEPTION: may throw by design (no window, blocked store) - `FromAccessor` catches. ERROR-SUPPRESSED-JUSTIFIED: the shared class answers "no storage". */
    private static LocalStorage = (): kKeyValueStorageLike | undefined => {
        return AppBrowserSettings.Window().localStorage;
    };

    /** DOCUMENTED EXCEPTION: may throw by design (no window, blocked store) - `FromAccessor` catches. ERROR-SUPPRESSED-JUSTIFIED: the shared class answers "no storage". */
    private static SessionStorage = (): kKeyValueStorageLike | undefined => {
        return AppBrowserSettings.Window().sessionStorage;
    };

    /** The page's `window`, reached through `globalThis` because the Bridge typecheck compiles this file WITHOUT the DOM lib (bare `window` = TS2304, R6 2026-10-10).
     *  DOCUMENTED EXCEPTION: absent in Node, so reading a property of it throws - `FromAccessor` catches. ERROR-SUPPRESSED-JUSTIFIED: the shared class answers "no storage". */
    private static Window = (): Record<string, kKeyValueStorageLike | undefined> => {
        return (globalThis as unknown as Record<string, Record<string, kKeyValueStorageLike | undefined>>).window;
    };

    /** DOCUMENTED EXCEPTION: may throw by design (a blocked accessor) - `FromAccessor` catches. ERROR-SUPPRESSED-JUSTIFIED: the shared class answers "no storage". */
    private static GlobalLocalStorage = (): kKeyValueStorageLike | undefined => {
        return (globalThis as unknown as Record<string, kKeyValueStorageLike | undefined>).localStorage;
    };
}
